<template>
  <BaseButton
    ref="buttonEl"
    :class="{ isEdited }"
    kind="auxiliary"
    :state="isEditable ? 'default' : 'disabled'"
    type="secondary"
    :text="formatedDate"
    @click="openDatePicker"
  >
    <template #icon>
      <TimeIcon />
    </template>
  </BaseButton>
  <DatePicker ref="datePickerEl" :modes="['date']" :show-clear="false" />
</template>
<script lang="ts">
import { format } from "date-fns/format";
import { computed, defineComponent, ref, toRefs } from "vue";

import type { MandateDates } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import DatePicker from "@/components/DatePicker.vue";
import TimeIcon from "@/components/icons/TimeIcon.vue";

export default defineComponent({
  components: {
    TimeIcon,
    BaseButton,
    DatePicker,
  },
  props: {
    date: { type: Date, required: true },
    isEditable: { type: Boolean, required: true },
    isEdited: { type: Boolean, required: true },
  },
  emits: {
    "update:date": (_date: MandateDates) => true,
  },
  setup(props, { emit }) {
    const { date, isEditable } = toRefs(props);
    const buttonEl = ref<InstanceType<typeof BaseButton>>();
    const datePickerEl = ref<InstanceType<typeof DatePicker>>();

    const formatDay = (time: Date) => {
      return format(time, "MMM, dd");
    };
    const formatedDate = computed<string>(() => formatDay(date.value));

    const openDatePicker = () => {
      if (!isEditable.value || !datePickerEl.value || !buttonEl.value) {
        return;
      }

      // Reclicking the date while its picker is open closes it (the picker's
      // own outside-click dismissal ignores clicks on the trigger).
      if (datePickerEl.value.isDisplayed) {
        datePickerEl.value.closePicker();
        return;
      }

      const element = buttonEl.value.$el;
      if (!(element instanceof HTMLElement)) return;
      datePickerEl.value.showPicker(
        element,
        date.value,
        (selection) => {
          if (selection?.mode === "date") {
            const mandateDates: MandateDates = {
              type: "date",
              date: selection.date,
            };
            emit("update:date", mandateDates);
          }
        },
        "left"
      );
    };

    return {
      buttonEl,
      datePickerEl,
      formatedDate,
      openDatePicker,
    };
  },
});
</script>
<style lang="scss" scoped>
.isEdited {
  @include themed() {
    color: t($tertiary-50) !important;
  }
}
</style>
